package org.multiplatform.kotfolio.pages.kotlin.new_feature

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.components.WebImage
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import org.multiplatform.kotfolio.util.Constants
import org.multiplatform.kotfolio.util.Resources

@Page("/kotlin/new_feature/2.2.0")
@Composable
fun NewFeature_2_2_0Page() {
    BlogLayout(
        PageProperties(
            "Kotlin 2.2.0",
            ""
        ),
        BlogProperties("kotlin", "Kotlin 2.2.0")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            KotlinCode(
                code = """
                    object Transform {
                        inline fun <T> call(block: () -> T): T {
                            return block()
                        }
                        
                        suspend inline fun <T> call (crossInline block: suspend () -> T): T 
                        {
                            return withContext(Dispatchers. IO) { block() }
                        }
                    }  
                      
                     fun getCached(): Profile = Transform.call {
                        cache.read()
                     }
                     
                     suspend fun getRemote(): Profile = Transform.call
                     suspend { //2.2.20, 2.3 -> default
                        api.fetchProfile()
                     })
                    """.trimIndent()
            )

            SpanText(
                text = "Context Parameters - (context receivers)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/LinkoraApp/Linkora.git
            KotlinCode(
                code = """
                    context(viewModel: ViewModel)
                    fun <T> Flow<T>.asStateInWhileSubscribed(
                        initialValue: T,
                        stopTimeoutMillis: Long = 5000
                    ): StateFlow<T> {
                        return stateIn(
                            scope = viewModel.viewModelScope,
                            started = SharingStarted.WhileSubscribed(stopTimeoutMillis = stopTimeoutMillis),
                            initialValue = initialValue
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Shared infrastructure in domain / use-case layer
                    
                    context(UserRepository)
                    suspend fun getUserName(id: String) =
                        getUser(id).name
                    
                    with(repository) {
                        getUserName("1")
                    }    
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                        context(OrderRepository, PaymentService, Logger)
                        class CreateOrderUseCase {
                            operator fun invoke(cart: Cart): Result<Order> {
                                logger.log("Creating order for cart: ${cart.id}")
                                
                                return try {
                                    val payment = paymentService.processPayment(cart.total)
                                    val order = Order.create(cart, payment)
                                    repository.save(order)
                                    Result.success(order)
                                } catch (e: Exception) {
                                    logger.error("Order failed: ${e.message}")
                                    Result.failure(e)
                                }
                            }
                        }

                        with(orderRepository) {
                            with(paymentService) {
                                with(logger) {
                                    val result = CreateOrderUseCase()(cart)
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //Multiple implicit dependencies
                    
                    context(User, Logger, Clock)
                    fun auditLogin() {
                        logger.log("${user.name} logged in at ${clock.now()}")
                    }
                    """.trimIndent()
            )

            //viewmodel.kt file open
            KotlinCode(
                code = $$"""
                    data class User(
                        val name: String
                    )

                    class Loggers {
                        fun log(message: String) {
                            print(message)
                        }
                    }

                    context(user:User, loggers:Loggers) // now can access both object's members inside the function,without calling the object
                    fun printUserInfo() {
                        loggers.log("User: ${user.name}")
                    }
                    """.trimIndent()
            )

            //https://github.com/AnatoliiChub/HRAM.git
            KotlinCode(
                code = """
                    import androidx.lifecycle.ViewModel
                    import androidx.lifecycle.viewModelScope
                    import kotlinx.coroutines.flow.Flow
                    import kotlinx.coroutines.flow.MutableStateFlow
                    import kotlinx.coroutines.flow.SharingStarted
                    import kotlinx.coroutines.flow.stateIn

                    private const val STATE_WHILE_SUBSCRIBED_TIMEOUT_MS = 5_000L

                    context(viewModel: ViewModel)
                    fun <T> MutableStateFlow<T>.stateInExt(initialValue: T) = stateIn(
                        scope = viewModel.viewModelScope,
                        started = SharingStarted.WhileSubscribed(STATE_WHILE_SUBSCRIBED_TIMEOUT_MS),
                        initialValue = initialValue,
                    )

                    context(viewModel: ViewModel)
                    fun <T> Flow<T>.stateInExt(initialValue: T) = stateIn(
                        scope = viewModel.viewModelScope,
                        started = SharingStarted.WhileSubscribed(STATE_WHILE_SUBSCRIBED_TIMEOUT_MS),
                        initialValue = initialValue,
                    )
                    
                    //ViewModel
                    private val _uiState = MutableStateFlow(RecordScreenState())
                    val uiState = _uiState.stateInExt(initialValue = RecordScreenState())
                    
                    data class RecordScreenState(
                        val bleNotification: BleNotification = BleNotification.Empty,
                        val trackingStatus: TrackingStatus = TrackingStatus(),
                        val recordingState: RecordingState = RecordingState.Init,
                        val requestBluetooth: Boolean = false,
                        val dialog: RecordScreenDialog? = null,
                    ) {
                        fun chooseHrDeviceDialog(duration: Duration) = this.copy(
                            dialog = RecordScreenDialog.ChooseHRDevice(
                                isLoading = true,
                                loadingDuration = duration
                            )
                        )
                    }

                    sealed class RecordScreenDialog {
                        data class ChooseHRDevice(
                            val isLoading: Boolean = false,
                            val scannedDevices: List<BleDevice> = emptyList(),
                            val loadingDuration: Duration,
                            val isDeviceConfirmed: Boolean = false
                        ) : RecordScreenDialog()

                        data class NameActivity(val activityName: String, val error: StringResource? = null) : RecordScreenDialog()

                        data class DeviceConnectedDialog(val bleDevice: BleDevice) : RecordScreenDialog()

                        data object OpenSettingsDialog : RecordScreenDialog()
                    }

                    fun MutableStateFlow<RecordScreenState>.updateHrDeviceDialogIfExists(
                        updatedDialog: (RecordScreenDialog.ChooseHRDevice) -> RecordScreenDialog.ChooseHRDevice
                    ) = (value.dialog as? RecordScreenDialog.ChooseHRDevice)?.let {
                        update { state -> state.copy(dialog = updatedDialog(it)) }
                    }

                    fun MutableStateFlow<RecordScreenState>.settingsDialog() =
                        update { it.copy(dialog = RecordScreenDialog.OpenSettingsDialog) }

                    fun MutableStateFlow<RecordScreenState>.deviceConnectedDialog(bleDevice: BleDevice) = update {
                        it.copy(
                            trackingStatus = it.trackingStatus.copy(trackHR = true, hrDevice = bleDevice),
                            dialog = RecordScreenDialog.DeviceConnectedDialog(bleDevice)
                        )
                    }

                    fun MutableStateFlow<RecordScreenState>.toggleHrTracking() = this.update {
                        it.copy(trackingStatus = it.trackingStatus.copy(trackHR = it.trackingStatus.trackHR.not(), hrDevice = null))
                    }

                    fun MutableStateFlow<RecordScreenState>.toggleRecordingState() =
                        this.update { it.copy(recordingState = if (it.recordingState.isRecording()) Paused else Recording) }

                    fun MutableStateFlow<RecordScreenState>.stop() = this.update { it.copy(recordingState = RecordingState.Init) }

                    fun MutableStateFlow<RecordScreenState>.requestBluetooth() =
                        this.update { it.copy(requestBluetooth = true, dialog = null) }

                    fun MutableStateFlow<RecordScreenState>.indications(bleNotification: BleNotification) =
                        this.update { it.copy(bleNotification = bleNotification) }

                    fun MutableStateFlow<RecordScreenState>.updateHrDeviceDialogConnecting() =
                        this.updateHrDeviceDialogIfExists { it.copy(isDeviceConfirmed = true, isLoading = true) }

                    val MutableStateFlow<RecordScreenState>.isRecording: Boolean get() = value.recordingState.isRecording()
                    """.trimIndent()
            )

            SpanText(
                text = "Smarter Overload Resolution with suspend Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun test() {
                        log { "Hello" }           // ✅ non-suspend version
                        log(suspend { "Hi" })     // ✅ suspend version
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "return in Expression Body Functions",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun getUserName(user: User?): String {
                        return if (user == null) "Guest" else user.name
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Smarter Exhaustiveness Checks in when",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://www.youtube.com/watch?v=QWpp5-LlTqA
            KotlinCode(
                code = """
                    kotlin {
                        compilerOptions {
                            freeCompilerArgs.add("-Xdata-flow-based-exhaustiveness")
                        }
                    }
                    
                    fun accessLevel(role: Role): Int {
                        if (role == Role.ADMIN) return 100
                        return when (role) {
                            Role.MEMBER -> 10
                            Role.GUEST -> 1
                        } // ✅ No else needed
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Reified Exceptions in catch",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    inline fun <reified E: Throwable> runCatching(block: () -> Unit) {
                        try {
                            block()
                        } catch (e: E) {   // ✅ Works now
                            println("Caught: ${e::class.simpleName}")
                        }
                    }

                    fun main() {
                        runCatching<IllegalArgumentException> {
                            throw IllegalArgumentException("Bad argument")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "context-sensitive resolution",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    enum class Problem {
                        CONNECTION, AUTHENTICATION, DATABASE, UNKNOWN
                    }

                    // Resolves enum entries based on the known type of problem
                    fun message(problem: Problem): String = when (problem) {
                        CONNECTION -> "connection"
                        AUTHENTICATION -> "authentication"
                        DATABASE -> "database"
                        UNKNOWN -> "unknown"
                    }
                    
                    kotlin {
                        compilerOptions {
                            freeCompilerArgs.add("-Xcontext-sensitive-resolution")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Support for return statements in expression bodies with explicit return types",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // Return type isn't explicitly specified, and the return statement is inside a lambda
                    // which will be deprecated
                    fun returnInsideLambda() = run { return 42 }

                    // Return type isn't explicitly specified, and the return statement is inside the initializer
                    // of a local variable, which will be deprecated
                    fun returnInsideIf() = when {
                        else -> {
                            val result = if (someCondition()) return "" else "value"
                            result
                        }
                    }
                    
                    kotlin {
                        compilerOptions {
                            languageVersion.set(org.jetbrains.kotlin.gradle.dsl.KotlinVersion.KOTLIN_2_3)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "contracts",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    kotlin {
                        compilerOptions {
                            freeCompilerArgs.add("-Xallow-contracts-on-more-functions")
                        }
                    }
                """.trimIndent()
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "CallsInPlace: The CallsInPlace contract is used to specify that a function parameter lambda is invoked in place. The different InvocationKind options are AT_MOST_ONCE, AT_LEAST_ONCE, EXACTLY_ONCE, and UNKNOWN.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @OptIn(ExperimentalContracts::class)
                    fun runOnlyOnce(block: () -> Unit) {
                        contract {
                            callsInPlace(block, InvocationKind.AT_LEAST_ONCE)
                        }
                        block()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlin.contracts.*

                    sealed class Failure {
                        class HttpError(val code: Int) : Failure()
                        // Insert other failure types here
                    }

                    sealed class Result<out T, out F : Failure> {
                        class Success<T>(val data: T) : Result<T, Nothing>()
                        class Failed<F : Failure>(val failure: F) : Result<Nothing, F>()
                    }

                    @OptIn(ExperimentalContracts::class)
                    // Uses a contract to assert a generic type
                    fun <T, F : Failure> Result<T, F>.isHttpError(): Boolean {
                        contract {
                            returns(true) implies (this@isHttpError is Result.Failed<Failure.HttpError>)
                        }
                        return this is Result.Failed && this.failure is Failure.HttpError
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(ExperimentalContracts:: class, ExperimentalExtendedContracts:: class)
                    fun <T: R, R> T.LetIf(condition: Boolean, block: (T) -> R: R {
                        contract {
                            callsInPlace(block, InvocationKind.AT_MOST_ONCE) 
                            condition.holdsIn(block)
                        ｝
                        return if (condition) {
                            block(this)
                        } else {
                            this
                        ｝
                    ｝
                    
                    // FLoW
                    val newFLow: Flow<Post> = postsFlow
                    •letIf(requiredTags != null) { it.filter { it.tags.containsAll(requiredTags)} }
                    .letIf(showOnlyImportant) { it.filter { it.important} }
                    
                    val modifier = Modifier
                    .letIf(onClick != null) { it.clickable(onClick = onclick) }
                    .letIf(isError) { it.background(Color(0xFFFFEBEE)) }
                    .letIf(selected) {it.border1.dp, Color(0x64556)) }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //1
                    fun Any?.isString(): Boolean {
                        contract {
                            returns(true) implies (this@isString is String)
                        }
                         return this is String
                    }
                    
                    //2
                    val Any.isHelloString: Boolean
                        get() {
                            @OptIn(ExperimentalContracts::class)
                            // Enables smart casting the receiver to String when the getter returns true
                            contract { returns(true) implies (this@isHelloString is String) }
                            return "hello" == this
                        }

                    fun printIfHelloString(x: Any) {
                        if (x.isHelloString) {
                            // Prints the length after the smart cast of the receiver to String
                            println(x.length)
                            // 5
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlin.contracts.*

                    @OptIn(ExperimentalContracts::class, ExperimentalExtendedContracts::class)
                    fun decode(encoded: String?): String? {
                        contract {
                            // Guarantees a non-null return value when the input is non-null
                            (encoded != null) implies (returnsNotNull())
                        }
                        if (encoded == null) return null
                        return java.net.URLDecoder.decode(encoded, "UTF-8")
                    }

                    fun useDecodedValue(s: String?) {
                        // Uses a safe call since the return value may be null
                        decode(s)?.length
                        
                        if (s != null) {
                            // Treats the return value as non-null after the smart cast
                            decode(s).length
                        }
                    }

                    fun main() {
                        val decoded = decode("Kotlin multiplatform")
                        println(decoded.length)
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "New approach for declaring common dependencies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    kotlin {
                        @OptIn(ExperimentalKotlinGradlePluginApi::class)
                        dependencies {
                            implementation("org.jetbrains.kotlinx:kotlinx-coroutines-core:1.10.2")
                        }
                    }
                    """.trimIndent()
            )


            SpanText(
                text = "Enable Interoperability Swift Export",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Interoperability with Swift has presented significant challenges, as Objective-C bridges hinder a truly native experience on iOS.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Swift Export solves this limitation by exporting Kotlin code directly to idiomatic Swift modules and without Objective-C intermediaries.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When using Swift Export, it is essential to understand how Kotlin declarations are translated from Kotlin to Swift. Since Swift Export is in an early stage of development, it currently only supports a subset of the Kotlin language.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            //https://www.youtube.com/watch?v=KEsVNrzPf24
            //KotlinBase - RunTime - To connect garbage collections
            //RCRef: a pointer to Kotlin world
            SpanText(
                text = "Swift Export Architecture",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Its architecture is designed to generate native Swift interfaces from Kotlin code, eliminating the need for Objective-C bridges.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There is a clear ABI separation between the compiler/runtime and the Swift export, allowing both parts to evolve independently.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Component release cycles can be managed separately. Transpile Kotlin to Swift.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ABI separation can be reused for other exports, such as C or Objective-C.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Generate Swift code to call Kotlin.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            WebImage(
                src = Resources.Images.SWIFT_EXPORT_BRIDGE,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Swift Export Bridge"
            )

            WebImage(
                src = Resources.Images.KOTLIN_TO_SWIFT,
                modifier = Modifier.fillMaxWidth().padding(5.px).borderRadius(10.px),
                contentDescription = "Swift Export Bridge"
            )

            KotlinCode(
                code = $$"""
                    //Update the Xcode Build Phase
                    //Build Phases -> Compile Kotlin Framework
                    
                    //1
                    if [ "YES" = "$OVERRIDE_KOTLIN_BUILD_IDE_SUPPORTED" ]; then
                      echo "Skipping Gradle build task invocation due to OVERRIDE_KOTLIN_BUILD_IDE_SUPPORTED environment variable set to \"YES\""
                      exit 0
                    fi
                    cd "$SRCROOT/.."
                    ./gradlew :shared:embedSwiftExportForXcode --configuration-cache
                    
                    //2
                    cd "$SRCROOT/.."
                    ./gradlew :playground:embedSwiftExportForXcode --no-build-cache
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Call Kotlin code from Swift Without Objective-C in the middle
                     
                    // Kotlin
                    enum class Color(val rgb: Int) {
                        RED(0xFF0000),
                        GREEN(0x00FF00),
                        BLUE(0x0000FF)
                    }

                    val color = Color.RED
                    
                    // Swift
                    public enum Color: Swift.CaseIterable, Swift.LosslessStringConvertible, Swift.RawRepresentable {
                        case RED, GREEN, BLUE

                        var rgb: Int { get }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Kotlin to Swift Direct
                     
                    //function 
                    fun addThreeNumber(a: Int, b: Int, c: Int): Int {
                        return a + b + c
                    }
                    Text("Total is: \(addThreeNumber(a: 10, b: 10, c:10))") //swift
                    
                    //List size/count
                    var programmingLanguages = listOf<String>("Kotlin", "Kotlin Multiplatform")
                    Text("Total programming languages are: \(programmingLanguages.count)") //swift
                    
                    //List mapOf
                    val programmingLanguages = listOf(
                        mapOf("id" to "1", "language" to "Kotlin"),
                        mapOf("id" to "2", "language" to "Kotlin Multiplatform")
                    )
                    Text("Total programming languages are: \(programmingLanguages.count)") //swift
                    """.trimIndent()
            )

            //https://github.com/GuilhE/SwiftExport-Playground.git
            KotlinCode(
                code = """
                    //https://github.com/fluttertutorialin/KotlinToSwift
                    
                    @file:OptIn(ExperimentalSwiftExportDsl::class)

                    import org.jetbrains.kotlin.gradle.plugin.mpp.apple.swiftexport.SWIFT_EXPORT_COROUTINES_SUPPORT_TURNED_ON
                    import org.jetbrains.kotlin.gradle.swiftexport.ExperimentalSwiftExportDsl
                    
                    kotlin {
                        iosArm64()
                        iosSimulatorArm64()

                        //Configure the Kotlin Build File
                        swiftExport {
                            moduleName = "ComposeApp"
                            flattenPackage = "com.playground"
                            configure { settings.put(SWIFT_EXPORT_COROUTINES_SUPPORT_TURNED_ON, "true") }
                        }

                        sourceSets {
                            commonMain.dependencies {
                                implementation(libs.kotlinx.coroutines.core)
                            }
                        }
                    }
                    
                    [versions]
                    android-compileSdk = "36"
                    android-minSdk = "24"
                    android-targetSdk = "36"
                    kotlin = "2.3.20-dev-2147"
                    kotlinxCoroutines = "1.10.2"

                    [libraries]
                    kotlinx-coroutines-core = { group = "org.jetbrains.kotlinx", name = "kotlinx-coroutines-core", version.ref = "kotlinxCoroutines" }

                    [plugins]
                    kotlinMultiplatform = { id = "org.jetbrains.kotlin.multiplatform", version.ref = "kotlin" }
                    
                    //CoroutinesExport.kt
                    data class DataClass(val value: String)

                    private val _stateFlow = MutableStateFlow(DataClass("Hello!"))
                    val stateFlow: StateFlow<DataClass> = _stateFlow.asStateFlow()

                    fun updateStateFlow(newValue: String) {
                        _stateFlow.value = DataClass(newValue)
                    }

                    private var collectJob: Job? = null
                    fun flowCollector(callback: (String) -> Unit) {
                        collectJob?.cancel().also { collectJob = null }
                        collectJob = CoroutineScope(Dispatchers.Default).launch(Dispatchers.Main) {
                            stateFlow.collect { callback(it.value) }
                        }
                    }

                    suspend fun suspendFunction(): DataClass {
                        delay(2000)
                        return DataClass("Hello from suspend fun")
                    }

                    private fun flowCreator(): Flow<DataClass> {
                        return flow {
                            emit(DataClass("Hello!"))
                            delay(1000)
                            emit(DataClass("SwiftExport"))
                            delay(1000)
                            emit(DataClass("Coroutines"))
                            delay(1000)
                            emit(DataClass("Are here!"))
                        }
                    }

                    private var flowJob: Job? = null
                    fun spawnCancelableCoroutine(callback: (String) -> Unit) {
                        cancelCoroutine()
                        flowJob = CoroutineScope(Dispatchers.Default).launch(Dispatchers.Main) {
                            flowCreator().collect { callback(it.value) }
                        }
                    }

                    fun cancelCoroutine() {
                        flowJob?.cancel()
                        flowJob = null
                    }

                    fun spawnCoroutine(callback: (String) -> Unit) {
                        CoroutineScope(Dispatchers.Default).launch(Dispatchers.Main) {
                            flowCreator().collect { callback(it.value) }
                        }
                    }
                    
                    //iosApp/ContentView.swift 
                    struct ContentView: View {
                        @State private var results: [String] = ["", "", "", ""]
                        @State private var isLoading: [Bool] = [false, false, false, false]
                        @State private var stateFlowValue: String = ""
                        @State private var flowEmissions: [String] = []
                        @State private var callbackEmissions: [String] = []
                        @State private var asyncStreamEmissions: [String] = []

                        private var animationTrigger: Int {
                            results.hashValue ^
                                flowEmissions.hashValue ^
                                callbackEmissions.hashValue ^
                                asyncStreamEmissions.hashValue
                        }

                        var body: some View {
                            ScrollViewReader { _ in
                                NavigationView {
                                    VStack(spacing: 20) {
                                        ScrollView {
                                            VStack(spacing: 15) {
                                                VStack(alignment: .leading, spacing: 10) {
                                                    HStack(spacing: 10) {
                                                        Button(action: { Task { await getStateFlowValue() } }) {
                                                            HStack {
                                                                if isLoading[0] {
                                                                    ProgressView()
                                                                        .scaleEffect(0.8)
                                                                }
                                                                Text("Get StateFlow")
                                                                Spacer()
                                                            }
                                                            .padding()
                                                            .background(Color.blue.opacity(0.1))
                                                            .cornerRadius(10)
                                                        }
                                                        .disabled(isLoading[0])

                                                        Button(action: { Task { await updateStateFlow() } }) {
                                                            HStack {
                                                                Text("Update StateFlow")
                                                                Spacer()
                                                            }
                                                            .padding()
                                                            .background(Color.green.opacity(0.1))
                                                            .cornerRadius(10)
                                                        }
                                                    }

                                                    if !results[0].isEmpty {
                                                        Text("Result:")
                                                            .font(.caption)
                                                            .foregroundColor(.secondary)
                                                        Text(results[0])
                                                            .padding()
                                                            .background(Color.gray.opacity(0.1))
                                                            .cornerRadius(8)
                                                            .font(.system(.body, design: .monospaced))
                                                    }
                                                }
                                                .padding(.horizontal)

                                                ForEach(0 ..< 4, id: \.self) { index in
                                                    VStack(alignment: .leading, spacing: 10) {
                                                        HStack(spacing: 10) {
                                                            Button(action: { Task { await coroutines(index: index) } }) {
                                                                HStack {
                                                                    if isLoading[index] {
                                                                        ProgressView().scaleEffect(0.8)
                                                                    }
                                                                    Text(getButtonTitle(for: index))
                                                                    Spacer()
                                                                }
                                                                .padding()
                                                                .background(Color.blue.opacity(0.1))
                                                                .cornerRadius(10)
                                                            }
                                                            .disabled(isLoading[index])

                                                            if index == 2 {
                                                                Button(action: {
                                                                    cancelCoroutine()
                                                                    results[index] = "Cancelled"
                                                                    isLoading[index] = false
                                                                }) {
                                                                    Text("Cancel")
                                                                        .padding()
                                                                        .background(Color.red.opacity(0.1))
                                                                        .cornerRadius(10)
                                                                }
                                                            }
                                                        }

                                                        if !results[index].isEmpty && index != 0 {
                                                            Text("Result:")
                                                                .font(.caption)
                                                                .foregroundColor(.secondary)
                                                            Text(results[index])
                                                                .frame(maxWidth: .infinity, alignment: .leading)
                                                                .padding()
                                                                .background(Color.gray.opacity(0.1))
                                                                .cornerRadius(8)
                                                                .font(.system(.body, design: .monospaced))
                                                        }

                                                        if index == 0 && !flowEmissions.isEmpty {
                                                            Text("Emissions:")
                                                                .font(.caption)
                                                                .foregroundColor(.secondary)
                                                            EmissionsListView(emissions: flowEmissions, color: Color.red)
                                                        }

                                                        if index == 2 && !callbackEmissions.isEmpty {
                                                            Text("Emissions:")
                                                                .font(.caption)
                                                                .foregroundColor(.secondary)
                                                            EmissionsListView(emissions: callbackEmissions, color: Color.green)
                                                        }

                                                        if index == 3 && !asyncStreamEmissions.isEmpty {
                                                            Text("Emissions:")
                                                                .font(.caption)
                                                                .foregroundColor(.secondary)
                                                            EmissionsListView(emissions: asyncStreamEmissions, color: Color.orange)
                                                        }
                                                    }
                                                    .padding(.horizontal)
                                                }
                                            }
                                        }
                                    }
                                    .navigationTitle("Kotlin SwiftExport")
                                    .navigationSubtitle("Coroutines tests")
                                    .animation(.easeInOut, value: animationTrigger)
                                    .toolbar {
                                        ToolbarItem(placement: .navigationBarTrailing) {
                                            Button(action: {
                                                results = ["", "", "", ""]
                                                stateFlowValue = ""
                                                flowEmissions = []
                                                callbackEmissions = []
                                                asyncStreamEmissions = []
                                            }) {
                                                Image(systemName: "trash").foregroundColor(.red)
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        private func getButtonTitle(for index: Int) -> String {
                            switch index {
                            case 0: return "Collect StateFlow"
                            case 1: return "suspend fun (2s delay)"
                            case 2: return "AsyncStream (restart-collect)"
                            case 3: return "AsyncStream (infinite spawn)"
                            default: return "Unknown Function"
                            }
                        }

                        private struct EmissionsListView: View {
                            let emissions: [String]
                            let color: Color
                            var body: some View {
                                let items: [(Int, String)] = emissions.enumerated().map { ($0.offset, $0.element) }
                                VStack(alignment: .leading, spacing: 4) {
                                    ForEach(items, id: \.0) { item in
                                        Text("\(item.0 + 1). \(item.1)")
                                            .font(.system(.caption, design: .monospaced))
                                            .padding(.horizontal, 8)
                                            .padding(.vertical, 2)
                                            .background(color.opacity(0.1))
                                            .cornerRadius(4)
                                    }
                                }
                                .padding()
                                .background(color.opacity(0.05))
                                .cornerRadius(8)
                            }
                        }

                        private func getStateFlowValue() async {
                            if let value = stateFlow.value {
                                let data = value as! DataClass
                                results[0] = "\(data.value)"
                            } else {
                                results[0] = ""
                            }
                        }

                        private func updateStateFlow() async {
                            com.playground.updateStateFlow(newValue: "Updated at \(Int(Date().timeIntervalSince1970))")
                        }

                        private func coroutines(index: Int) async {
                            switch index {
                            case 0:
                                flowCollector { value in flowEmissions.append(value) }

                            case 1:
                                isLoading[index] = true
                                let res = await suspendFunction()
                                results[index] = res.value
                                isLoading[index] = false

                            case 2:
                                callbackEmissions = []
                                results[index] = "Collecting emissions..."
                                spawnCancelableCoroutine { value in
                                    callbackEmissions.append(value)
                                    results[index] = "Collected \(self.callbackEmissions.count) emission(s)\nLatest: \(value)"
                                }

                            case 3:
                                asyncStreamEmissions = []
                                results[index] = "Collecting emissions..."
                                for await value in toAsyncStream(spawnCoroutine) {
                                    asyncStreamEmissions.append(value)
                                    results[index] = "Collected \(asyncStreamEmissions.count) emission(s)\nLatest: \(value)"
                                }

                            default: results[index] = "Function not found"
                            }
                        }

                        private func toAsyncStream(_ callback: (@escaping (String) -> Void) -> Void) -> AsyncStream<String> {
                            AsyncStream { continuation in
                                callback { value in continuation.yield(value) }
                            }
                        }
                    }

                    #Preview {
                        ContentView()
                    }
                    """.trimIndent()
            )
        }
    }
}
//https://www.youtube.com/watch?v=QWpp5-LlTqA
//https://itnext.io/kotlin-2-3-unused-return-value-checker-fewer-swallowed-results-more-predictability-6cb4f3fa94d9

























